import React, { useState, useEffect, useRef } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import api from '@/api';
import { toast } from 'sonner';
import {
  Palette, Upload, X, Check, RotateCcw, Lock, Building2,
  Globe, Eye, Loader2, Image as ImageIcon, Sparkles,
  Copy, ExternalLink, ShieldCheck, AlertCircle,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const DEFAULT_FORM = {
  brand_name: '',
  logo_url: '',
  primary_color: '#c9a84c',
  accent_color: '',
  hide_ostivex_branding: false,
  powered_by_text: '',
  support_email: '',
  support_url: '',
  custom_domain: '',
};

// ── Preview mini sidebar ─────────────────────────────────────────────────────
function SidebarPreview({ form, slug }) {
  const primary = form.primary_color || '#c9a84c';
  const name = form.brand_name || slug || 'Il tuo brand';
  const logoSrc = form.logo_url
    ? (form.logo_url.startsWith('http') ? form.logo_url : `${BACKEND}${form.logo_url}`)
    : null;

  return (
    <div className="rounded-lg overflow-hidden border border-[var(--lux-border-1)]" style={{ background: 'var(--lux-surface-1)' }}>
      <div className="px-4 py-3 border-b border-[var(--lux-border-1)]" style={{ borderLeftColor: primary, borderLeftWidth: 3 }}>
        {logoSrc ? (
          <img src={logoSrc} alt={name} className="h-7 object-contain max-w-[140px]"
            onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <span className="font-serif text-base font-light" style={{ color: primary }}>{name}</span>
        )}
        {!form.hide_ostivex_branding && (
          <p className="text-[9px] mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>Shangrilux Luxury Hospitality</p>
        )}
        {form.hide_ostivex_branding && form.powered_by_text && (
          <p className="text-[9px] mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>{form.powered_by_text}</p>
        )}
      </div>
      <div className="py-2 space-y-0.5">
        {['Dashboard', 'Prenotazioni', 'Chiavi Virtuali', 'Impostazioni'].map(item => (
          <div key={item} className="px-4 py-1.5 text-xs" style={{ color: 'var(--lux-text-muted-2)' }}>{item}</div>
        ))}
      </div>
    </div>
  );
}

// ── Preview portale ospiti ────────────────────────────────────────────────────
function PortalPreview({ form, slug }) {
  const primary = form.primary_color || '#c9a84c';
  const name = form.brand_name || slug || 'Il tuo brand';
  const logoSrc = form.logo_url
    ? (form.logo_url.startsWith('http') ? form.logo_url : `${BACKEND}${form.logo_url}`)
    : null;

  return (
    <div className="rounded-lg overflow-hidden border border-[var(--lux-border-1)]" style={{ background: 'var(--lux-bg)' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--lux-border-1)]">
        <div className="flex items-center gap-3">
          {logoSrc ? (
            <img src={logoSrc} alt={name} className="h-6 object-contain max-w-[100px]"
              onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <span className="font-serif text-sm font-light text-white">{name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--lux-text-muted-3)' }}>IT</span>
          <div className="w-5 h-5 rounded-full flex items-center justify-center"
            style={{ background: `${primary}20`, color: primary }}>
            <Globe size={11} />
          </div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-1.5">
        <div className="h-2 rounded-full w-3/4" style={{ background: 'var(--lux-surface-3)' }} />
        <div className="h-2 rounded-full w-1/2" style={{ background: 'var(--lux-surface-3)' }} />
        <div className="mt-3 h-7 rounded text-[10px] flex items-center justify-center font-medium"
          style={{ background: primary, color: '#000' }}>
          Prenota ora
        </div>
      </div>
    </div>
  );
}

// ── Custom Domain Section ─────────────────────────────────────────────────────
function CustomDomainSection({ domain, onChange, onSave, saving }) {
  const [workerScript, setWorkerScript] = useState('');
  const [nginxConfig, setNginxConfig] = useState('');
  const [dnsInstructions, setDnsInstructions] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [activeScript, setActiveScript] = useState('cloudflare');

  const loadConfig = async () => {
    if (!domain) { toast.error('Inserisci prima il dominio custom'); return; }
    setLoadingConfig(true);
    try {
      const res = await api.get('/branding/proxy-config');
      setWorkerScript(res.data.cloudflare_worker);
      setNginxConfig(res.data.nginx_config);
      setDnsInstructions(res.data.dns_instructions);
      setShowScript(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel recupero della configurazione');
    } finally {
      setLoadingConfig(false);
    }
  };

  const verify = async () => {
    if (!domain) { toast.error('Inserisci prima il dominio'); return; }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.get('/domain/verify', { params: { domain } });
      setVerifyResult(res.data);
    } catch (err) {
      setVerifyResult({
        overall: { status: 'red', message: err.response?.data?.detail || 'Verifica fallita' },
        checks: {},
      });
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copiato negli appunti'));
  };

  return (
    <div className="card-dark rounded-lg p-5 space-y-4" data-testid="branding-custom-domain-card">
      <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
        <Globe size={15} style={{ color: 'var(--lux-gold)' }} />
        <span className="text-sm font-medium text-white">Dominio Custom</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-widest font-medium ml-auto"
          style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)' }}>
          Tutti i piani White-Label
        </span>
      </div>

      <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>
        Permetti agli ospiti di accedere al portale prenotazioni con il tuo dominio
        (es. <span style={{ color: 'var(--lux-gold)' }}>booking.tuohotel.com</span>) invece del nostro URL.
        Funziona tramite Cloudflare Worker o Nginx reverse proxy.
      </p>

      <Field label="Il tuo dominio custom" hint="Solo il dominio, senza https:// (es. booking.tuohotel.com)">
        <div className="flex gap-2">
          <input
            type="text"
            data-testid="branding-custom-domain-input"
            value={domain}
            onChange={e => onChange(e.target.value.replace(/https?:\/\//g, ''))}
            placeholder="booking.tuohotel.com"
            className="flex-1 px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)] font-mono"
          />
          <button
            data-testid="branding-get-config-btn"
            onClick={async () => { await onSave(); await loadConfig(); }}
            disabled={loadingConfig || saving || !domain}
            className="px-3 py-2 rounded text-xs font-medium transition-opacity disabled:opacity-40 shrink-0"
            style={{ border: '1px solid var(--lux-gold)', color: 'var(--lux-gold)' }}>
            {loadingConfig ? <Loader2 size={12} className="animate-spin" /> : 'Genera Script'}
          </button>
        </div>
      </Field>

      {/* Verifica DNS */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="branding-verify-domain-btn"
          onClick={verify}
          disabled={verifying || !domain}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-opacity disabled:opacity-40"
          style={{ background: 'var(--lux-surface-2)', color: 'var(--lux-text-muted-2)', border: '1px solid var(--lux-border-1)' }}>
          {verifying ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
          Verifica dominio
        </button>
        {verifyResult?.overall && (
          <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded"
            style={{
              background: verifyResult.overall.status === 'green' ? 'rgba(34,197,94,0.1)'
                        : verifyResult.overall.status === 'yellow' ? 'rgba(234,179,8,0.1)'
                        : 'rgba(239,68,68,0.1)',
              color: verifyResult.overall.status === 'green' ? '#22c55e'
                   : verifyResult.overall.status === 'yellow' ? '#eab308'
                   : '#ef4444',
            }}>
            {verifyResult.overall.status === 'green' && <Check size={12} />}
            {verifyResult.overall.status !== 'green' && <AlertCircle size={12} />}
            {verifyResult.overall.message}
          </div>
        )}
      </div>

      {/* Semaforo dettagliato: DNS · Cloudflare · SSL */}
      {verifyResult?.checks && Object.keys(verifyResult.checks).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="branding-verify-checks">
          {[
            { key: 'dns', label: 'DNS' },
            { key: 'cloudflare', label: 'Cloudflare' },
            { key: 'ssl', label: 'SSL / HTTPS' },
          ].map(({ key, label }) => {
            const c = verifyResult.checks[key];
            if (!c) return null;
            const color = c.status === 'green' ? '#22c55e'
                        : c.status === 'yellow' ? '#eab308'
                        : '#ef4444';
            const bg = c.status === 'green' ? 'rgba(34,197,94,0.06)'
                     : c.status === 'yellow' ? 'rgba(234,179,8,0.06)'
                     : 'rgba(239,68,68,0.06)';
            return (
              <div key={key} className="p-3 rounded"
                data-testid={`branding-verify-check-${key}`}
                style={{ background: bg, border: `1px solid ${color}40` }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                  <span className="text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--lux-text-muted)' }}>{label}</span>
                </div>
                <p className="text-xs font-medium mb-1" style={{ color }}>{c.title}</p>
                <p className="text-[11px] mb-1" style={{ color: 'var(--lux-text-muted-2)' }}>{c.detail}</p>
                {c.hint && (
                  <p className="text-[10px] italic mt-1.5 pt-1.5" style={{ color: 'var(--lux-text-muted-3)', borderTop: '1px dashed rgba(255,255,255,0.06)' }}>
                    💡 {c.hint}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Script generato */}
      {showScript && workerScript && (
        <div className="space-y-3 mt-2">
          {/* Tab selector */}
          <div className="flex gap-1 p-1 rounded w-fit" style={{ background: 'var(--lux-surface-2)' }}>
            {[
              { id: 'cloudflare', label: 'Cloudflare Worker' },
              { id: 'nginx', label: 'Nginx Config' },
            ].map(opt => (
              <button key={opt.id} onClick={() => setActiveScript(opt.id)}
                className="px-3 py-1 rounded text-xs font-medium transition-all"
                style={{
                  background: activeScript === opt.id ? 'var(--lux-surface-3)' : 'transparent',
                  color: activeScript === opt.id ? 'white' : 'var(--lux-text-muted-3)',
                }}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Script area */}
          <div className="relative">
            <pre className="text-[10px] font-mono p-3 rounded overflow-auto max-h-52 leading-relaxed"
              style={{ background: 'var(--lux-bg)', color: '#7ee787', border: '1px solid var(--lux-border-4)' }}>
              {activeScript === 'cloudflare' ? workerScript : nginxConfig}
            </pre>
            <button
              onClick={() => copyToClipboard(activeScript === 'cloudflare' ? workerScript : nginxConfig)}
              className="absolute top-2 right-2 p-1.5 rounded text-xs flex items-center gap-1"
              style={{ background: 'var(--lux-surface-2)', color: 'var(--lux-text-muted-2)' }}>
              <Copy size={11} />
              Copia
            </button>
          </div>

          {/* DNS instructions */}
          {dnsInstructions && (
            <div className="rounded p-3 space-y-1.5 text-xs"
              style={{ background: 'rgba(201,168,76,0.05)', border: '1px dashed rgba(201,168,76,0.3)' }}>
              <p className="font-medium" style={{ color: 'var(--lux-gold)' }}>Istruzioni DNS</p>
              <p style={{ color: 'var(--lux-text-muted)' }}>
                <strong>Opzione A (Cloudflare):</strong> {dnsInstructions.option_a_cloudflare}
              </p>
              <p style={{ color: 'var(--lux-text-muted)' }}>
                <strong>Opzione B (Nginx):</strong> {dnsInstructions.option_b_nginx}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                {dnsInstructions.notes}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-white">{label}</label>
      {hint && <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>{hint}</p>}
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function BrandingPage() {
  const { tenant, features, slug, refreshTenant, loading: tenantLoading } = useTenant();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const fileRef = useRef(null);

  const plan = tenant?.plan || '';
  const planLabel = tenant?.features?.plan_label_it || plan || '—';
  const canWhiteLabel = features?.white_label === true;
  const isElite = features?.white_label_full === true;

  useEffect(() => {
    if (!canWhiteLabel) { setLoading(false); return; }
    api.get('/branding').then(res => {
      const b = res.data.branding || {};
      setForm({
        brand_name: b.brand_name || '',
        logo_url: b.logo_url || '',
        primary_color: b.primary_color || '#c9a84c',
        accent_color: b.accent_color || '',
        hide_ostivex_branding: b.hide_ostivex_branding || false,
        powered_by_text: b.powered_by_text || '',
        support_email: b.support_email || '',
        support_url: b.support_url || '',
        custom_domain: b.custom_domain || '',
      });
      if (b.logo_url) setLogoPreview(b.logo_url.startsWith('http') ? b.logo_url : `${BACKEND}${b.logo_url}`);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [canWhiteLabel]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const path = res.data.path;
      set('logo_url', path);
      setLogoPreview(`${BACKEND}${path}?t=${Date.now()}`);
      toast.success('Logo caricato con successo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel caricamento del logo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        brand_name: form.brand_name || null,
        logo_url: form.logo_url || null,
        primary_color: form.primary_color || null,
        ...(isElite ? {
          accent_color: form.accent_color || null,
          hide_ostivex_branding: form.hide_ostivex_branding,
          powered_by_text: form.powered_by_text || null,
        } : {}),
        support_email: form.support_email || null,
        support_url: form.support_url || null,
        custom_domain: form.custom_domain || null,
      };
      await api.put('/branding', payload);
      await refreshTenant?.();
      toast.success('Branding salvato con successo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Sei sicuro di voler ripristinare il branding di default? Tutte le personalizzazioni verranno eliminate.')) return;
    try {
      await api.delete('/branding');
      setForm(DEFAULT_FORM);
      setLogoPreview('');
      await refreshTenant?.();
      toast.success('Branding ripristinato');
    } catch (err) {
      toast.error('Errore nel ripristino');
    }
  };

  // Mostra spinner se il tenant context è ancora in caricamento
  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--lux-gold)' }} />
      </div>
    );
  }

  // ── Locked state ─────────────────────────────────────────────────────────────
  if (!canWhiteLabel) {
    return (
      <div data-testid="branding-locked" className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ background: 'var(--lux-surface-2)' }}>
          <Lock size={28} style={{ color: 'var(--lux-text-muted-3)' }} />
        </div>
        <h2 className="text-xl font-serif text-white mb-2">White-Label non disponibile</h2>
        <p className="text-sm mb-6" style={{ color: 'var(--lux-text-muted)' }}>
          Il branding personalizzato è disponibile a partire dal piano <strong>Professional</strong>.
        </p>
        <div className="flex gap-3">
          <span className="px-4 py-2 rounded border text-sm font-medium" style={{ borderColor: 'var(--lux-gold)', color: 'var(--lux-gold)' }}>
            Piano attuale: {planLabel}
          </span>
          <a href="mailto:info@shangrilux.com?subject=Upgrade%20Professional"
            className="px-4 py-2 rounded text-sm font-medium text-black"
            style={{ background: 'var(--lux-gold)' }}>
            Richiedi upgrade
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--lux-gold)' }} />
      </div>
    );
  }

  return (
    <div data-testid="branding-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Palette size={18} style={{ color: 'var(--lux-gold)' }} />
            <h1 className="text-white text-xl font-light font-serif">Branding & White-Label</h1>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-widest"
              style={{ background: isElite ? 'rgba(201,168,76,0.15)' : 'rgba(148,163,184,0.15)', color: isElite ? 'var(--lux-gold)' : '#94a3b8' }}>
              {isElite ? 'Elite' : 'Professional'}
            </span>
          </div>
          <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
            Personalizza il brand che vedranno gli ospiti nel portale e nell&apos;app.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleReset}
            data-testid="branding-reset-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs transition-colors"
            style={{ color: 'var(--lux-text-muted-3)', border: '1px solid var(--lux-border-1)' }}>
            <RotateCcw size={13} />
            Ripristina default
          </button>
          <button onClick={handleSave} disabled={saving}
            data-testid="branding-save-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium transition-opacity disabled:opacity-50"
            style={{ background: 'var(--lux-gold)', color: '#000' }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? 'Salvataggio...' : 'Salva branding'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* ── Form (left) ───────────────────────────────────────── */}
        <div className="xl:col-span-3 space-y-5">

          {/* Identità brand */}
          <div className="card-dark rounded-lg p-5 space-y-5" data-testid="branding-identity-card">
            <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
              <Building2 size={15} style={{ color: 'var(--lux-gold)' }} />
              <span className="text-sm font-medium text-white">Identità Brand</span>
            </div>

            {/* Logo upload */}
            <Field label="Logo aziendale" hint="PNG, SVG o WebP — max 2MB. Apparirà nel pannello admin, portale e app ospiti.">
              <div className="flex items-start gap-3">
                {/* Preview + remove */}
                <div className="shrink-0 w-16 h-16 rounded border flex items-center justify-center overflow-hidden relative"
                  style={{ borderColor: 'var(--lux-border-1)', background: 'var(--lux-surface-2)' }}>
                  {logoPreview ? (
                    <>
                      <img src={logoPreview} alt="logo preview" className="w-full h-full object-contain p-1"
                        onError={() => setLogoPreview('')} />
                      <button onClick={() => { set('logo_url', ''); setLogoPreview(''); }}
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500/80 flex items-center justify-center">
                        <X size={9} color="#fff" />
                      </button>
                    </>
                  ) : (
                    <ImageIcon size={22} style={{ color: 'var(--lux-text-muted-3)' }} />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    data-testid="branding-upload-logo-btn"
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded border text-sm transition-colors disabled:opacity-50"
                    style={{ borderColor: 'var(--lux-border-4)', color: 'var(--lux-text-muted-2)', borderStyle: 'dashed' }}>
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploading ? 'Caricamento...' : 'Carica file logo'}
                  </button>
                  <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp,.gif"
                    onChange={handleFileUpload} className="hidden" />
                  <p className="text-xs text-center" style={{ color: 'var(--lux-text-muted-3)' }}>oppure</p>
                  <input
                    type="url"
                    data-testid="branding-logo-url-input"
                    value={form.logo_url.startsWith('/uploads') ? '' : form.logo_url}
                    onChange={e => {
                      set('logo_url', e.target.value);
                      setLogoPreview(e.target.value);
                    }}
                    placeholder="https://example.com/logo.png"
                    className="w-full px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]"
                  />
                </div>
              </div>
            </Field>

            {/* Nome brand */}
            <Field label="Nome brand" hint="Sostituisce 'Shangrilux' nell'intestazione del pannello.">
              <input
                type="text"
                data-testid="branding-name-input"
                value={form.brand_name}
                onChange={e => set('brand_name', e.target.value)}
                placeholder={tenant?.name || 'Il tuo hotel'}
                className="w-full px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]"
              />
            </Field>

            {/* Colore primario */}
            <Field label="Colore primario" hint="Usato per CTA, link e accenti in tutto il portale ospiti.">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="color"
                    data-testid="branding-color-picker"
                    value={form.primary_color || '#c9a84c'}
                    onChange={e => set('primary_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[var(--lux-border-1)] bg-transparent"
                    style={{ padding: 2 }}
                  />
                </div>
                <input
                  type="text"
                  data-testid="branding-color-input"
                  value={form.primary_color}
                  onChange={e => set('primary_color', e.target.value)}
                  placeholder="#c9a84c"
                  maxLength={7}
                  className="flex-1 px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)] font-mono"
                />
                <div className="w-8 h-8 rounded border border-[var(--lux-border-1)]"
                  style={{ background: form.primary_color || '#c9a84c' }} />
              </div>
            </Field>
          </div>

          {/* Opzioni avanzate — Elite */}
          {isElite ? (
            <div className="card-dark rounded-lg p-5 space-y-5" data-testid="branding-advanced-card">
              <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
                <Sparkles size={15} style={{ color: 'var(--lux-gold)' }} />
                <span className="text-sm font-medium text-white">Opzioni Avanzate</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-widest font-medium"
                  style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)' }}>Elite</span>
              </div>

              <Field label="Colore accento" hint="Colore secondario per hover, bordi e badge.">
                <div className="flex items-center gap-3">
                  <input type="color" value={form.accent_color || '#ffffff'}
                    onChange={e => set('accent_color', e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-[var(--lux-border-1)] bg-transparent" style={{ padding: 2 }} />
                  <input type="text" value={form.accent_color} onChange={e => set('accent_color', e.target.value)}
                    placeholder="#ffffff" maxLength={7}
                    className="flex-1 px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)] font-mono" />
                </div>
              </Field>

              <div className="flex items-center justify-between py-2 px-3 rounded"
                style={{ background: 'var(--lux-surface-2)' }}>
                <div>
                  <p className="text-sm text-white">Nascondi branding Shangrilux</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted-3)' }}>
                    Rimuove &quot;Shangrilux Luxury Hospitality&quot; dalla sidebar e dal footer
                  </p>
                </div>
                <button
                  data-testid="branding-hide-ostivex-toggle"
                  onClick={() => set('hide_ostivex_branding', !form.hide_ostivex_branding)}
                  className="relative w-10 h-5 rounded-full transition-colors"
                  style={{ background: form.hide_ostivex_branding ? 'var(--lux-gold)' : 'var(--lux-border-4)' }}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.hide_ostivex_branding ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              {form.hide_ostivex_branding && (
                <Field label="Testo 'Powered by'" hint="Mostrato sotto il logo al posto di 'Shangrilux Luxury Hospitality'. Lascia vuoto per nascondere tutto.">
                  <input type="text" value={form.powered_by_text}
                    onChange={e => set('powered_by_text', e.target.value)}
                    placeholder="Powered by Il Tuo Brand"
                    className="w-full px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]" />
                </Field>
              )}
            </div>
          ) : (
            /* Teaser Elite per Professional */
            <div className="card-dark rounded-lg p-4 border border-dashed border-[var(--lux-border-4)] flex items-center gap-4">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'rgba(201,168,76,0.1)' }}>
                <Lock size={15} style={{ color: 'var(--lux-gold)' }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Opzioni Elite</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>
                  Con Elite puoi rimuovere il branding Shangrilux, personalizzare il testo &quot;Powered by&quot; e scegliere il colore accento.
                </p>
              </div>
              <a href="mailto:info@shangrilux.com?subject=Upgrade%20Elite"
                className="shrink-0 text-xs px-3 py-1.5 rounded font-medium"
                style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--lux-gold)' }}>
                Upgrade
              </a>
            </div>
          )}

          {/* Supporto (tutti i piani white-label) */}
          <div className="card-dark rounded-lg p-5 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-[var(--lux-border-1)]">
              <Globe size={15} style={{ color: 'var(--lux-gold)' }} />
              <span className="text-sm font-medium text-white">Supporto Brand</span>
            </div>
            <Field label="Email supporto" hint="Mostrata nell'app ospiti come contatto.">
              <input type="email" value={form.support_email} onChange={e => set('support_email', e.target.value)}
                placeholder="info@tuohotel.com"
                className="w-full px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]" />
            </Field>
            <Field label="Sito web supporto" hint="Link al sito del brand (es. tuohotel.com).">
              <input type="url" value={form.support_url} onChange={e => set('support_url', e.target.value)}
                placeholder="https://tuohotel.com"
                className="w-full px-3 py-2 rounded text-sm bg-[var(--lux-surface-2)] border border-[var(--lux-border-1)] text-white placeholder:text-[var(--lux-text-muted-3)] focus:outline-none focus:border-[var(--lux-gold)]" />
            </Field>
          </div>

          {/* Dominio Custom */}
          <CustomDomainSection
            domain={form.custom_domain}
            onChange={v => set('custom_domain', v)}
            onSave={handleSave}
            saving={saving}
          />
        </div>

        {/* ── Anteprima Live (right) ────────────────────────────── */}
        <div className="xl:col-span-2 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <div className="flex items-center gap-2">
            <Eye size={14} style={{ color: 'var(--lux-gold)' }} />
            <span className="text-sm font-medium text-white">Anteprima Live</span>
            <span className="text-xs ml-auto" style={{ color: 'var(--lux-text-muted-3)' }}>Aggiornata in tempo reale</span>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted-3)' }}>Pannello Admin — Sidebar</p>
              <SidebarPreview form={form} slug={slug} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--lux-text-muted-3)' }}>Portale Prenotazioni — Header</p>
              <PortalPreview form={form} slug={slug} />
            </div>
          </div>

          <div className="rounded-lg p-3 text-xs space-y-1"
            style={{ background: 'var(--lux-surface-2)', color: 'var(--lux-text-muted)' }}>
            <p className="font-medium text-white">Come funziona</p>
            <p>Il branding si applica automaticamente al pannello admin, al portale prenotazioni ospiti e all&apos;app guest PWA.</p>
            <p className="mt-1">Le modifiche diventano attive subito dopo il salvataggio.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
